/* side-nav.tsx generated by @compiled/babel-plugin v0.36.1 */
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./side-nav.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { bind } from 'bind-event-listener';
import { flushSync } from 'react-dom';
import { OpenLayerObserver, useOpenLayerObserver } from '@atlaskit/layering/experimental/open-layer-observer';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { media } from '@atlaskit/primitives/responsive';
import { HomeActionsElement } from '../../../context/home-actions/home-actions-context';
import { useSkipLinkInternal } from '../../../context/skip-links/skip-links-context';
import { contentHeightWhenFixed, contentInsetBlockStart, localSlotLayers, sideNavVar, UNSAFE_sideNavLayoutVar } from '../constants';
import { DangerouslyHoistSlotSizes } from '../hoist-slot-sizes-context';
import { DangerouslyHoistCssVarToDocumentRoot } from '../hoist-utils';
import { PanelSplitterProvider } from '../panel-splitter/provider';
import { usePrefixedUID } from '../use-prefixed-id';
import { sideNavFlyoutCloseDelayMs } from './flyout-close-delay-ms';
import { SideNavToggleButtonElement } from './toggle-button-context';
import { useExpandSideNav } from './use-expand-side-nav';
import { useSideNavVisibility } from './use-side-nav-visibility';
import { useSideNavVisibilityCallbacks } from './use-side-nav-visibility-callbacks';
import { useToggleSideNav } from './use-toggle-side-nav';
import { SetSideNavVisibilityState, SideNavVisibilityState } from './visibility-context';
var panelSplitterResizingVar = '--n_snvRsz';
var widthResizeBounds = {
  min: '240px',
  max: '50vw'
};
var styles = {
  root: "_nd5l1b6c _t51zzgxb _bfhk1bhr _16qs1cd0 _vchhusvi _4t3i1run _152t1l7u _kqsw1if8 _1bsb1ego _1pbycs5v _14b5hc79 _qilnk0mc _4ap3vuon _scbpglyw",
  flyoutOpen: "_qiln1gzg _4ap31bhr _scbp1cd0 _139f8iot _1tpvfnf5 _sylc1a5r _oyeijq3t",
  flyoutAnimateClosed: "_1e0cglyw _qiln1gzg _139f8iot _1tpvfnf5 _sylc1a5r _1uwsjq3t",
  flexContainer: "_4t3i1osq _1e0c1txw _2lx21bp4 _1bah1yb4",
  hiddenMobileAndDesktop: "_1e0cglyw",
  hiddenMobileOnly: "_1e0cglyw _dm2518uv",
  hiddenDesktopOnly: "_dm25glyw"
};
/**
 * We need an additional component layer so we can wrap the side nav in a `OpenLayerObserver` and have access to the
 * context value.
 */
function SideNavInternal(_ref) {
  var children = _ref.children,
    defaultCollapsed = _ref.defaultCollapsed,
    _ref$defaultWidth = _ref.defaultWidth,
    defaultWidth = _ref$defaultWidth === void 0 ? 320 : _ref$defaultWidth,
    testId = _ref.testId,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'Sidebar' : _ref$label,
    _ref$skipLinkLabel = _ref.skipLinkLabel,
    skipLinkLabel = _ref$skipLinkLabel === void 0 ? label : _ref$skipLinkLabel,
    onExpand = _ref.onExpand,
    onCollapse = _ref.onCollapse,
    id = _ref.id;
  var UID = usePrefixedUID();
  var CID = id ? id : UID;
  var expandSideNav = useExpandSideNav();
  /**
   * Called after clicking on the side nav skip link, and ensures the side nav is expanded so that it is focusable.
   *
   * We need to update the DOM synchronously because `.focus()` is called synchronously after this state update.
   */
  var synchronouslyExpandSideNav = useCallback(function () {
    flushSync(function () {
      /**
       * Calling this unconditionally and relying on it to avoid no-op renders.
       *
       * We _could_ call it conditionally, but we'd be duplicating the screen size checks `expandSideNav` makes.
       */
      expandSideNav();
    });
  }, [expandSideNav]);
  useSkipLinkInternal({
    id: CID,
    label: skipLinkLabel,
    onBeforeNavigate: synchronouslyExpandSideNav
  });
  var sideNavState = useContext(SideNavVisibilityState);
  var setSideNavState = useContext(SetSideNavVisibilityState);
  var _useSideNavVisibility = useSideNavVisibility({
      defaultCollapsed: defaultCollapsed
    }),
    isExpandedOnDesktop = _useSideNavVisibility.isExpandedOnDesktop,
    isExpandedOnMobile = _useSideNavVisibility.isExpandedOnMobile;
  // We are placing `defaultCollapsed` into a state container so we can have a stable reference to the initial value.
  // This is so we can use it in an effect _that only runs once_, after the initial render on the client,
  // to sync the side nav context (provided in `<Root>`) with the `defaultCollapsed` prop provided to `<SideNav>`.
  var _useState = useState(defaultCollapsed),
    _useState2 = _slicedToArray(_useState, 1),
    initialDefaultCollapsed = _useState2[0];
  var _useState3 = useState(defaultWidth),
    _useState4 = _slicedToArray(_useState3, 2),
    width = _useState4[0],
    setWidth = _useState4[1];
  var clampedWidth = "clamp(".concat(widthResizeBounds.min, ", ").concat(width, "px, ").concat(widthResizeBounds.max, ")");
  var dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);
  var navRef = useRef(null);
  var toggleButtonElement = useContext(SideNavToggleButtonElement);
  var homeActionsElement = useContext(HomeActionsElement);
  var devTimeOnlyAttributes = {};
  var openLayerObserver = useOpenLayerObserver();
  var flyoutStateRef = useRef({
    type: 'not-active'
  });
  var isFlyoutVisible = (sideNavState === null || sideNavState === void 0 ? void 0 : sideNavState.flyout) === 'open';
  var updateFlyoutState = useMemo(function () {
    function tryAbortPendingClose() {
      if (flyoutStateRef.current.type === 'waiting-for-close') {
        flyoutStateRef.current.abort();
      }
    }
    function open() {
      tryAbortPendingClose();
      flyoutStateRef.current = {
        type: 'open'
      };
      setSideNavState(function (currentState) {
        if ((currentState === null || currentState === void 0 ? void 0 : currentState.desktop) === 'collapsed' && (currentState === null || currentState === void 0 ? void 0 : currentState.flyout) !== 'open') {
          return {
            desktop: currentState.desktop,
            mobile: currentState.mobile,
            flyout: 'open'
          };
        }
        return currentState;
      });
    }
    function close() {
      tryAbortPendingClose();
      flyoutStateRef.current = {
        type: 'not-active'
      };
      setSideNavState(function (currentState) {
        if ((currentState === null || currentState === void 0 ? void 0 : currentState.desktop) === 'collapsed' && (currentState === null || currentState === void 0 ? void 0 : currentState.flyout) === 'open') {
          return {
            desktop: currentState.desktop,
            mobile: currentState.mobile,
            flyout: 'triggered-animate-close'
          };
        }
        return currentState;
      });
    }
    return function onAction(action) {
      if (action === 'drag-from-flyout-started') {
        open();
        flyoutStateRef.current = {
          type: 'is-dragging-from-flyout'
        };
        return;
      }
      if (action === 'drag-from-flyout-finished') {
        open();
        return;
      }

      // ignoring all actions until the drag is finished
      if (flyoutStateRef.current.type === 'is-dragging-from-flyout') {
        return;
      }
      if (action === 'open') {
        open();
        return;
      }
      if (action === 'waiting-for-close') {
        if (flyoutStateRef.current.type === 'waiting-for-close') {
          return;
        }

        // A timeout is used to close the flyout after a delay when the user mouses out of the flyout area, and to allow
        // us to cancel the close if the user mouses back in.
        var timeout = setTimeout(function () {
          updateFlyoutState('ready-to-close');
        }, sideNavFlyoutCloseDelayMs);
        flyoutStateRef.current = {
          type: 'waiting-for-close',
          abort: function abort() {
            clearTimeout(timeout);
          }
        };
        return;
      }
      if (action === 'ready-to-close') {
        // If there are no open layers, we can close the flyout.
        if (openLayerObserver.getCount() === 0) {
          close();
          return;
        }
        flyoutStateRef.current = {
          type: 'ready-to-close'
        };
        return;
      }
      if (action === 'force-close') {
        close();
        return;
      }
    };
  }, [openLayerObserver, setSideNavState]);
  var toggleVisibility = useToggleSideNav();
  useEffect(function () {
    // Sync the visibility in context (provided in `<Root>`) with the local `defaultCollapsed` prop provided to `SideNav`
    // after SSR hydration. This should only run once, after the initial render on the client.
    setSideNavState({
      desktop: initialDefaultCollapsed ? 'collapsed' : 'expanded',
      mobile: 'collapsed',
      flyout: 'closed'
    });
  }, [initialDefaultCollapsed, setSideNavState]);
  var handleExpand = useCallback(function (_ref2) {
    var screen = _ref2.screen;
    onExpand === null || onExpand === void 0 || onExpand({
      screen: screen
    });

    // When the side nav gets expanded, we close the flyout to reset it.
    // This prevents the flyout from staying open and ensures we are respecting the user's intent to expand.
    updateFlyoutState('force-close');
  }, [onExpand, updateFlyoutState]);
  var handleCollapse = useCallback(function (_ref3) {
    var screen = _ref3.screen;
    onCollapse === null || onCollapse === void 0 || onCollapse({
      screen: screen
    });

    // When the side nav gets collapsed, we close the flyout to reset it.
    // This prevents the flyout from staying open and ensures we are respecting the user's intent to collapse.
    updateFlyoutState('force-close');
  }, [onCollapse, updateFlyoutState]);
  useSideNavVisibilityCallbacks({
    onExpand: handleExpand,
    onCollapse: handleCollapse,
    isExpandedOnDesktop: isExpandedOnDesktop,
    isExpandedOnMobile: isExpandedOnMobile
  });
  useEffect(function () {
    var mediaQueryList = window.matchMedia('(min-width: 64rem)');
    return bind(mediaQueryList, {
      type: 'change',
      listener: function listener() {
        if (mediaQueryList.matches) {
          // We're transitioning from tablet to desktop viewport size.
          // We forcibly show the side nav if it was shown on mobile.
          if (isExpandedOnMobile && !isExpandedOnDesktop) {
            toggleVisibility();
          }
        }
      }
    });
  }, [toggleVisibility, isExpandedOnDesktop, isExpandedOnMobile]);
  useEffect(function () {
    if (!toggleButtonElement) {
      return;
    }
    return bind(toggleButtonElement, {
      type: 'mouseenter',
      listener: function listener() {
        // Only flyout the side nav if the user hovered while the side nav was collapsed
        if (isExpandedOnDesktop) {
          return;
        }

        // Only flyout the side nav on desktop viewports
        var _window$matchMedia = window.matchMedia('(min-width: 64rem)'),
          matches = _window$matchMedia.matches;
        if (matches) {
          updateFlyoutState('open');
        }
      }
    });
  }, [updateFlyoutState, toggleButtonElement, isExpandedOnDesktop]);
  useEffect(function () {
    if (!toggleButtonElement) {
      return;
    }
    return bind(toggleButtonElement, {
      type: 'mouseleave',
      listener: function listener() {
        // If the side nav is not in flyout mode, we don't need to do anything
        if (!isFlyoutVisible) {
          return;
        }
        updateFlyoutState('waiting-for-close');
      }
    });
  }, [isFlyoutVisible, toggleButtonElement, updateFlyoutState]);
  useEffect(function () {
    if (!navRef.current) {
      return;
    }
    return bind(navRef.current, {
      type: 'mouseenter',
      listener: function listener() {
        // If the side nav is not in flyout mode, we don't need to do anything
        if (isExpandedOnDesktop || !isFlyoutVisible) {
          return;
        }

        // The user mouses has moused back over the side nav
        updateFlyoutState('open');
      }
    });
  }, [isFlyoutVisible, updateFlyoutState, isExpandedOnDesktop]);
  useEffect(function () {
    if (!navRef.current) {
      return;
    }
    return bind(navRef.current, {
      type: 'mouseleave',
      listener: function listener() {
        // If the side nav is not in flyout mode, we don't need to do anything
        if (!isFlyoutVisible) {
          return;
        }
        updateFlyoutState('waiting-for-close');
      }
    });
  }, [isFlyoutVisible, updateFlyoutState]);
  useEffect(function () {
    var nav = navRef.current;
    if (!nav) {
      return;
    }
    if (!isFlyoutVisible) {
      return;
    }

    // Using a monitor rather than a drop target. Rationale:
    // - We are not creating a drop target for the users to drop on,
    //   we are just interested in listening to events within an element
    // - We do not want to impact `location.{*}.dropTargets` in events
    return monitorForElements({
      canMonitor: function canMonitor(_ref4) {
        var source = _ref4.source;
        return nav.contains(source.element);
      },
      onGenerateDragPreview: function onGenerateDragPreview() {
        updateFlyoutState('drag-from-flyout-started');
      },
      onDrop: function onDrop(_ref5) {
        var location = _ref5.location;
        // Always marking the drag and done
        updateFlyoutState('drag-from-flyout-finished');

        // If the user dropped outside of the flyout, we will close the flyout
        var underUsersPointer = document.elementFromPoint(location.current.input.clientX, location.current.input.clientY);
        if (!nav.contains(underUsersPointer)) {
          updateFlyoutState('waiting-for-close');
        }
      }
    });
  }, [isFlyoutVisible, updateFlyoutState]);
  useEffect(function () {
    if (!homeActionsElement || !toggleButtonElement) {
      return;
    }
    return bind(homeActionsElement, {
      type: 'mouseover',
      listener: function listener(event) {
        // If the side nav is not in flyout mode, we don't need to do anything
        if (isExpandedOnDesktop || !isFlyoutVisible) {
          return;
        }
        if (event.target === homeActionsElement) {
          // The mouse is directly over the home actions container, so cancel any pending flyout closes.
          updateFlyoutState('open');
          return;
        }
        if (event.target instanceof Element && toggleButtonElement.contains(event.target)) {
          // The mouse is over the toggle button or any of its children, so we don't want to close the flyout.
          // We also don't need to cancel any pending closes, as we have separate event listeners for the toggle button mouse events.
          return;
        }

        // The user has moused over a child element of the home actions container that isn't the toggle button, e.g. the app switcher or nav logo,
        // so we should close the flyout (with a delay).
        updateFlyoutState('waiting-for-close');
      }
    });
  }, [homeActionsElement, isFlyoutVisible, toggleButtonElement, isExpandedOnDesktop, updateFlyoutState]);
  useEffect(function () {
    if (!homeActionsElement) {
      return;
    }
    return bind(homeActionsElement, {
      type: 'mouseleave',
      listener: function listener() {
        // If the side nav is not in flyout mode, we don't need to do anything
        if (!isFlyoutVisible) {
          return;
        }

        // The mouse has left the home actions container, so we should close the flyout with a delay.
        updateFlyoutState('waiting-for-close');
      }
    });
  }, [homeActionsElement, isFlyoutVisible, updateFlyoutState]);
  useEffect(function () {
    // Close the flyout if there are no more layers open and the user is not mousing over the
    // flyout areas (side nav, top bar home actions element)
    return openLayerObserver.onChange(function onChange(_ref6) {
      var count = _ref6.count;
      if (flyoutStateRef.current.type === 'ready-to-close' && count === 0) {
        updateFlyoutState('force-close');
      }
    });
  }, [openLayerObserver, updateFlyoutState]);
  useEffect(function () {
    // Clear flyout close timer if being unmounted
    return function cleanupPendingFlyoutClose() {
      if (flyoutStateRef.current.type === 'waiting-for-close') {
        flyoutStateRef.current.abort();
      }
    };
  }, []);
  if (process.env.NODE_ENV !== 'production') {
    var visible = [];
    if (isExpandedOnMobile) {
      visible.push('small');
    }
    if (isExpandedOnDesktop) {
      visible.push('large');
    }
    if (isFlyoutVisible) {
      visible.push('flyout');
    }
    devTimeOnlyAttributes['data-visible'] = visible.length ? visible.join(',') : 'false';
  }
  return /*#__PURE__*/React.createElement("nav", _extends({
    id: CID
  }, devTimeOnlyAttributes, {
    "data-layout-slot": true,
    "aria-label": label,
    style: _defineProperty({}, sideNavVar, clampedWidth),
    ref: navRef,
    "data-testid": testId,
    className: ax([styles.root, isExpandedOnDesktop && !isExpandedOnMobile && !isFlyoutVisible && styles.hiddenMobileOnly, !isExpandedOnDesktop && isExpandedOnMobile && !isFlyoutVisible && styles.hiddenDesktopOnly, !isExpandedOnDesktop && !isExpandedOnMobile && !isFlyoutVisible && styles.hiddenMobileAndDesktop, (sideNavState === null || sideNavState === void 0 ? void 0 : sideNavState.flyout) === 'open' && styles.flyoutOpen, (sideNavState === null || sideNavState === void 0 ? void 0 : sideNavState.flyout) === 'triggered-animate-close' && styles.flyoutAnimateClosed])
  }), dangerouslyHoistSlotSizes &&
  /*#__PURE__*/
  // ------ START UNSAFE STYLES ------
  // These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
  // When they aren't needed anymore we can delete them wholesale.
  React.createElement(DangerouslyHoistCssVarToDocumentRoot, {
    variableName: UNSAFE_sideNavLayoutVar,
    value: "0px",
    mediaQuery: media.above.md,
    responsiveValue: isExpandedOnDesktop ? clampedWidth : 0
  })
  // ------ END UNSAFE STYLES ------
  , /*#__PURE__*/React.createElement(PanelSplitterProvider, {
    panelRef: navRef,
    panelWidth: width,
    onCompleteResize: setWidth,
    resizeBounds: widthResizeBounds,
    resizingCssVar: panelSplitterResizingVar,
    isEnabled: isExpandedOnDesktop && !isFlyoutVisible
  }, /*#__PURE__*/React.createElement("div", {
    className: ax([styles.flexContainer])
  }, children)));
}
export function SideNav(_ref8) {
  var children = _ref8.children,
    defaultCollapsed = _ref8.defaultCollapsed,
    _ref8$defaultWidth = _ref8.defaultWidth,
    defaultWidth = _ref8$defaultWidth === void 0 ? 320 : _ref8$defaultWidth,
    testId = _ref8.testId,
    label = _ref8.label,
    _ref8$skipLinkLabel = _ref8.skipLinkLabel,
    skipLinkLabel = _ref8$skipLinkLabel === void 0 ? label : _ref8$skipLinkLabel,
    onExpand = _ref8.onExpand,
    onCollapse = _ref8.onCollapse,
    id = _ref8.id;
  return /*#__PURE__*/React.createElement(OpenLayerObserver, null, /*#__PURE__*/React.createElement(SideNavInternal, {
    defaultCollapsed: defaultCollapsed,
    defaultWidth: defaultWidth,
    testId: testId,
    label: label,
    skipLinkLabel: skipLinkLabel,
    onExpand: onExpand,
    onCollapse: onCollapse,
    id: id
  }, children));
}